import { Controller } from "@hotwired/stimulus"
import Dropzone from "dropzone";
import I18n from "../../i18n/setup.js.erb";


import {
  removeElement,
  insertAfter
} from "../components/shared/dom_helpers";
import { parameterizeFilename } from "../components/shared/stringUtils";

export default class extends Controller {
  static targets = ["input", "dropzoneClickable", "editBtn", "uploadBtn"];

  connect() {
    this.maxFiles =this.element.dataset.maxFiles || 1;
    this.addRemoveLinks =this.element.dataset.addRemoveLinks || false;
    this.maxFileSize =this.element.dataset.maxFileSize || 256; // in MB
    this.acceptedFiles =this.element.dataset.acceptedFiles || ".pdf,.png,.jpg,.jpeg";
    this.directUploadData = JSON.parse(this.inputTarget.dataset.directUploadData);
    this.dropzoneFieldName =this.element.dataset.dropzoneFieldName
    this.dropZone = createDropZone(this);
    this.hideFileInput();
    this.bindEvents();
    Dropzone.autoDiscover = false; // necessary quirk for Dropzone error in console
  }

  clickDropzone(e) {
    e.preventDefault();
    this.dropZone.removeAllFiles(true)
    this.dropzoneClickableTarget.click();
  }

  // Private
  hideFileInput() {
    this.inputTarget.disabled = true;
    this.inputTarget.style.display = "none";
  }

  bindEvents() {
    this.dropZone.on("addedfile", file => {
      setTimeout(() => {
        file.accepted && createDirectUploadController(this, file).start();
      }, 500);
    });

    this.dropZone.on("removedfile", file => {
      file.controller && removeElement(file.controller.hiddenInput);
    });

    this.dropZone.on("canceled", file => {
      file.controller && file.controller.xhr.abort();
    });
  }
}

class DirectUploadController {
  constructor(source, file) {
    this.directUploadData =  source.directUploadData;
    this.source = source;
    this.file = file;
    this.dropzoneFieldName = source.dropzoneFieldName;
    this.editBtn = source.editBtnTarget;
    this.uploadBtn = source.uploadBtnTarget;
  }

  start() {
    this.file.controller = this;
    this.hiddenInput = this.createHiddenInput();
    this.postToS3(this.file, this.directUploadData).then( response => {
      if (response.status != 201 ) {
        removeElement(this.hiddenInput);
        this.emitDropzoneError(I18n.t("dropzone.error"));
      } else {
        this.hiddenInput.value = this.filePath;
        this.emitDropzoneSuccess();
      }

    });
  }
  createHiddenInput() {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = this.source.inputTarget.name;
    insertAfter(input, this.source.inputTarget);
    return input;
  }

  async postToS3(file, directUploadData) {
    this.emitDropzoneUploading();
    const fields = directUploadData.fields;
    const filename = parameterizeFilename(file.name)
    this.filePath = fields.key.replace("${filename}",filename)
    const formData = new FormData();
    formData.append("X-Amz-Algorithm", fields["X-Amz-Algorithm"]);
    formData.append("X-Amz-Credential", fields["X-Amz-Credential"]);
    formData.append("X-Amz-Date", fields["X-Amz-Date"]);
    formData.append("X-Amz-Signature", fields["X-Amz-Signature"]);
    formData.append("key", this.filePath);
    formData.append("policy", fields["policy"]);
    formData.append("success_action_status", fields["success_action_status"]);
    formData.append("file", file);
    const response = await fetch(directUploadData.url, {
      body: formData,
      method: "POST",
    });
    return response
  }

  emitDropzoneUploading() {
    this.file.status = Dropzone.UPLOADING;
    this.source.dropZone.emit("processing", this.file);
    const event = new CustomEvent("dropzone:upload:processing");
    this.source.dropZone.element.parentElement.dispatchEvent(event);
  }

  emitDropzoneError(error) {
    this.file.status = Dropzone.ERROR;
    this.source.dropZone.emit("error", this.file, error);
    this.source.dropZone.emit("complete", this.file);
    const event = new CustomEvent("dropzone:upload:error", {detail: {error: error}});
    this.source.dropZone.element.parentElement.dispatchEvent(event);
  }

  emitDropzoneSuccess() {
    this.file.status = Dropzone.SUCCESS;
    this.source.dropZone.emit("success", this.file);
    this.source.dropZone.emit("complete", this.file);
    const event = new CustomEvent("dropzone:upload:success", { 
      detail: { 
        fieldName: this.dropzoneFieldName, 
        filePath: this.filePath 
      } 
    });
    this.source.dropZone.element.parentElement.dispatchEvent(event);
    this.editBtn.classList.remove("d-none");
    this.uploadBtn.classList.add("d-none");
  }
}

function createDirectUploadController(source, file) {
  return new DirectUploadController(source, file);
}

function createDropZone(controller) {
  return new Dropzone(controller.element.querySelector(".dropzone"), {
    url: controller.directUploadData.url,
    method: "put",
    headers: controller.headers,
    maxFiles: controller.maxFiles,
    maxFilesize: controller.maxFileSize,
    acceptedFiles: controller.acceptedFiles,
    addRemoveLinks: controller.addRemoveLinks,
    autoQueue: false,
    dictInvalidFileType: I18n.t("dropzone.invalid_file_type_error", {acceptedFiles: controller.acceptedFiles}),
    dictFileTooBig: I18n.t("dropzone.file_to_large_error", {maxFilesize: controller.maxFileSize}),
    dictResponseError: I18n.t("dropzone.error"),
    dictRemoveFile: I18n.t("dropzone.remove_file"),
    dictCancelUpload: I18n.t("dropzone.cancel_upload"),
    dictCancelUploadConfirmation: I18n.t("dropzone.cancel_upload_confirmation"),
    dictMaxFilesExceeded: I18n.t("dropzone.max_files_exceeded", {maxFiles: controller.maxFiles}),
  });
}
